import React, { useEffect, useState } from 'react';
import { DuplicateDetectionService, PotentialDuplicate, Student, MergeRequest } from '../services/DuplicateDetectionService';

interface DuplicateDetectionProps {
  onDuplicatesChanged?: () => void;
}

export const DuplicateDetection: React.FC<DuplicateDetectionProps> = ({ onDuplicatesChanged }) => {
  const [duplicates, setDuplicates] = useState<PotentialDuplicate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState<PotentialDuplicate | null>(null);
  const [comparing, setComparing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDuplicates();
  }, []);

  const loadDuplicates = async () => {
    setLoading(true);
    setError(null);
    try {
      const potentialDuplicates = await DuplicateDetectionService.findPotentialDuplicates();
      setDuplicates(potentialDuplicates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (duplicate: PotentialDuplicate) => {
    setSelectedDuplicate(duplicate);
    setComparing(true);
  };

  const handleMergeStudents = async (keepStudentId: number, mergeStudentIds: number[], mergedData: Partial<Student>) => {
    setMerging(true);
    try {
      const mergeRequest: MergeRequest = {
        keepStudentId,
        mergeStudentIds,
        mergedData
      };
      
      await DuplicateDetectionService.mergeStudents(mergeRequest);
      
      // Reload duplicates after merge
      await loadDuplicates();
      setComparing(false);
      setSelectedDuplicate(null);
      
      if (onDuplicatesChanged) {
        onDuplicatesChanged();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge students');
    } finally {
      setMerging(false);
    }
  };

  const handleMarkAsNotDuplicate = async (duplicate: PotentialDuplicate) => {
    try {
      const studentIds = duplicate.students.map(s => s.id);
      await DuplicateDetectionService.markAsNotDuplicate(studentIds);
      
      // Remove from current list
      setDuplicates(duplicates.filter(d => d.id !== duplicate.id));
      
      if (selectedDuplicate?.id === duplicate.id) {
        setComparing(false);
        setSelectedDuplicate(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark as not duplicate');
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return '#dc3545'; // Red - very likely duplicate
    if (confidence >= 0.8) return '#fd7e14'; // Orange - likely duplicate  
    return '#ffc107'; // Yellow - possible duplicate
  };

  const getMatchTypeLabel = (matchType: string) => {
    switch (matchType) {
      case 'email_exact': return 'Exact Email Match';
      case 'name_fuzzy': return 'Similar Name';
      case 'phone_match': return 'Phone Number Match';
      default: return matchType;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ color: 'white', fontSize: '18px' }}>
          Analyzing students for potential duplicates...
        </div>
        <div style={{ color: '#ccc', marginTop: '10px' }}>
          This may take a moment for large datasets
        </div>
      </div>
    );
  }

  if (comparing && selectedDuplicate) {
    return (
      <StudentComparison 
        duplicate={selectedDuplicate}
        onMerge={handleMergeStudents}
        onMarkAsNotDuplicate={() => handleMarkAsNotDuplicate(selectedDuplicate)}
        onCancel={() => {
          setComparing(false);
          setSelectedDuplicate(null);
        }}
        merging={merging}
      />
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ color: 'white', margin: '0 0 10px 0' }}>Duplicate Detection</h2>
          <p style={{ color: '#ccc', margin: 0 }}>
            Found {duplicates.length} potential duplicate groups affecting {duplicates.reduce((sum, d) => sum + d.students.length, 0)} students
          </p>
        </div>
        <button
          onClick={loadDuplicates}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          disabled={loading}
        >
          🔄 Refresh Analysis
        </button>
      </div>

      {error && (
        <div style={{
          padding: '15px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          Error: {error}
        </div>
      )}

      {duplicates.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '50px',
          backgroundColor: '#d1edff',
          color: '#0c5460',
          borderRadius: '8px',
          border: '1px solid #bee5eb'
        }}>
          <h3 style={{ margin: '0 0 10px 0' }}>✅ No Duplicates Found</h3>
          <p style={{ margin: 0 }}>
            All students appear to be unique. No potential duplicates were detected.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {duplicates.map((duplicate) => (
            <div
              key={duplicate.id}
              style={{
                border: `2px solid ${getConfidenceColor(duplicate.confidence)}`,
                borderRadius: '8px',
                backgroundColor: '#2a2a2a',
                overflow: 'hidden'
              }}
            >
              <div style={{
                backgroundColor: getConfidenceColor(duplicate.confidence),
                color: 'white',
                padding: '12px 15px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>{getMatchTypeLabel(duplicate.matchType)}</strong>
                  <span style={{ marginLeft: '10px', opacity: 0.9 }}>
                    Confidence: {(duplicate.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleViewDetails(duplicate)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    Compare & Merge
                  </button>
                  <button
                    onClick={() => handleMarkAsNotDuplicate(duplicate)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    Not Duplicate
                  </button>
                </div>
              </div>
              
              <div style={{ padding: '15px' }}>
                <div style={{ color: '#ccc', marginBottom: '15px', fontSize: '0.95em' }}>
                  {duplicate.reason}
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                  {duplicate.students.map((student) => (
                    <div
                      key={student.id}
                      style={{
                        backgroundColor: '#333',
                        padding: '12px',
                        borderRadius: '6px',
                        border: '1px solid #555'
                      }}
                    >
                      <div style={{ color: 'white', fontWeight: 'bold', marginBottom: '8px' }}>
                        {student.combined_name}
                        {student.gender && (
                          <span style={{ 
                            marginLeft: '8px', 
                            color: student.gender === 'Male' ? '#87CEEB' : '#FFB6C1',
                            fontSize: '0.9em' 
                          }}>
                            ({student.gender === 'Male' ? 'M' : 'F'})
                          </span>
                        )}
                      </div>
                      <div style={{ color: '#ccc', fontSize: '0.9em' }}>
                        <div><strong>Email:</strong> {student.email}</div>
                        {student.phone_number && <div><strong>Phone:</strong> {student.phone_number}</div>}
                        {student.semester_year && <div><strong>Semester:</strong> {student.semester_year}</div>}
                        {student.major && <div><strong>Major:</strong> {student.major}</div>}
                        <div><strong>Created:</strong> {new Date(student.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface StudentComparisonProps {
  duplicate: PotentialDuplicate;
  onMerge: (keepStudentId: number, mergeStudentIds: number[], mergedData: Partial<Student>) => Promise<void>;
  onMarkAsNotDuplicate: () => void;
  onCancel: () => void;
  merging: boolean;
}

const StudentComparison: React.FC<StudentComparisonProps> = ({ 
  duplicate, 
  onMerge, 
  onMarkAsNotDuplicate, 
  onCancel,
  merging
}) => {
  const [keepStudentId, setKeepStudentId] = useState<number>(duplicate.students[0].id);
  const [mergedData, setMergedData] = useState<Partial<Student>>({});

  const keepStudent = duplicate.students.find(s => s.id === keepStudentId)!;
  const mergeStudents = duplicate.students.filter(s => s.id !== keepStudentId);

  const handleFieldSelection = (field: keyof Student, value: any) => {
    setMergedData(prev => ({ ...prev, [field]: value }));
  };

  const handleMerge = async () => {
    const mergeStudentIds = mergeStudents.map(s => s.id);
    await onMerge(keepStudentId, mergeStudentIds, mergedData);
  };

  const renderFieldComparison = (fieldName: keyof Student, label: string) => {
    const keepValue = keepStudent[fieldName];
    const otherValues = mergeStudents.map(s => s[fieldName]).filter(v => v !== keepValue && v !== null && v !== '');
    
    if (otherValues.length === 0) return null;

    const selectedValue = mergedData[fieldName] !== undefined ? mergedData[fieldName] : keepValue;

    return (
      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: 'white', display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          {label}:
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ color: '#ccc', cursor: 'pointer' }}>
            <input
              type="radio"
              name={fieldName}
              checked={selectedValue === keepValue}
              onChange={() => handleFieldSelection(fieldName, keepValue)}
              style={{ marginRight: '8px' }}
            />
            {String(keepValue)} <span style={{ fontSize: '0.8em', opacity: 0.7 }}>(from {keepStudent.combined_name})</span>
          </label>
          {otherValues.map((value, index) => (
            <label key={index} style={{ color: '#ccc', cursor: 'pointer' }}>
              <input
                type="radio"
                name={fieldName}
                checked={selectedValue === value}
                onChange={() => handleFieldSelection(fieldName, value)}
                style={{ marginRight: '8px' }}
              />
              {String(value)} <span style={{ fontSize: '0.8em', opacity: 0.7 }}>
                (from {mergeStudents.find(s => s[fieldName] === value)?.combined_name})
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ color: 'white', margin: '0 0 10px 0' }}>Compare and Merge Students</h2>
        <p style={{ color: '#ccc' }}>
          Select which student to keep as the primary record and choose the best data for each field.
        </p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: 'white', display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
          Primary Student (keep this record):
        </label>
        <select
          value={keepStudentId}
          onChange={(e) => setKeepStudentId(Number(e.target.value))}
          style={{
            padding: '8px',
            backgroundColor: '#333',
            color: 'white',
            border: '1px solid #666',
            borderRadius: '4px',
            minWidth: '300px'
          }}
        >
          {duplicate.students.map(student => (
            <option key={student.id} value={student.id}>
              {student.combined_name} - {student.email}
            </option>
          ))}
        </select>
      </div>

      <div style={{
        backgroundColor: '#2a2a2a',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: 'white', marginBottom: '20px' }}>Field Selection</h3>
        
        {renderFieldComparison('email', 'Email')}
        {renderFieldComparison('phone_number', 'Phone Number')}
        {renderFieldComparison('address', 'Address')}
        {renderFieldComparison('home_country', 'Home Country')}
        {renderFieldComparison('major', 'Major')}
        {renderFieldComparison('program_level', 'Program Level')}
        {renderFieldComparison('marital_status', 'Marital Status')}
        {renderFieldComparison('special_interests', 'Special Interests')}
        {renderFieldComparison('dietary_restrictions_details', 'Dietary Restrictions')}
      </div>

      <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          style={{
            padding: '10px 20px',
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          disabled={merging}
        >
          Cancel
        </button>
        
        <button
          onClick={onMarkAsNotDuplicate}
          style={{
            padding: '10px 20px',
            backgroundColor: '#ffc107',
            color: '#000',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          disabled={merging}
        >
          Mark as Not Duplicate
        </button>
        
        <button
          onClick={handleMerge}
          style={{
            padding: '10px 20px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: merging ? 0.7 : 1
          }}
          disabled={merging}
        >
          {merging ? 'Merging...' : `Merge ${mergeStudents.length} Students`}
        </button>
      </div>
    </div>
  );
};